import { apiFetch } from './client.js';

/**
 * The parent's side of a child's sign-in (ruled 14-16 October): ask for the
 * setup email, read and finish the setup from its link, deactivate it, and the
 * first password: whether the child has signed in, and a new one-time password
 * (ruled 17 October).
 *
 * The setup is addressed by the SHA-256 of the emailed token, worked out here,
 * so the token itself never sits in a URL the API logs. It goes in the body of
 * the one request that finishes the setup.
 */

type GetToken = () => Promise<string | null>;

export interface SignInWords {
  intro: string;
  password: string;
  agreement: string;
}

export interface SetupView {
  client_id: string;
  first_name: string;
  last_name: string;
  birth_year: number;
  /** Set when turning back on: the username never changes. */
  username: string | null;
  suggested_username: string;
  again: boolean;
  parent_email: string;
  words: SignInWords;
}

export async function setupHash(token: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function requestSetupEmail(getToken: GetToken, clientId: string): Promise<{ sent_to: string | null }> {
  return apiFetch(`/api/people/${encodeURIComponent(clientId)}/athlete-login/setup-emails`, getToken, { method: 'POST' });
}

export function readSetup(getToken: GetToken, hash: string): Promise<SetupView> {
  return apiFetch(`/api/athlete-login-setups/${hash}`, getToken);
}

export function completeSetup(
  getToken: GetToken,
  hash: string,
  body: { token: string; username: string | null; words: SignInWords },
): Promise<{ client_id: string; username: string; one_time_password: string | null }> {
  return apiFetch(`/api/athlete-login-setups/${hash}/consents`, getToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function turnOffSignIn(getToken: GetToken, clientId: string): Promise<{ enabled: false; turned_off_at: string }> {
  return apiFetch(`/api/people/${encodeURIComponent(clientId)}/athlete-login`, getToken, {
    method: 'PUT',
    body: JSON.stringify({ enabled: false }),
  });
}

export function readFirstSignIn(getToken: GetToken, clientId: string): Promise<{ signed_in: boolean; at: string | null }> {
  return apiFetch(`/api/people/${encodeURIComponent(clientId)}/athlete-login/first-sign-in`, getToken);
}

export function newOneTimePassword(getToken: GetToken, clientId: string): Promise<{ one_time_password: string }> {
  return apiFetch(`/api/people/${encodeURIComponent(clientId)}/athlete-login/one-time-passwords`, getToken, { method: 'POST' });
}
